import type { TicketBoardCard, TicketDetail, TicketKarte, TicketSubtaskRow } from "@kundeninfo/api-client";
import { tagBeschriftung } from "./ticketFormat.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";

/**
 * REGELN DER OBERFLÄCHE FÜR TEILAUFGABEN (Spec Teilaufgaben R11, R15, R24, R25, R34) — reine Funktionen. Die
 * Strichzahl rechnet das Web (Festlegung 4 des Plans: `web` importiert `domain` nicht); die Anzeige-Nummer kommt vom
 * Server. Dazu der Hinweis „verlieren den Zugriff" beim Teamwechsel, der seit R7/R12 Eigentümer und Teilbaum nennt.
 */

export const MAX_STROKES = 3;
/** Festlegung 5 des Plans: Breite des Trefferstreifens in px je Strichzahl (mindestens 16); am Telefon `--treffer`. */
export const STRIPE_PX: readonly number[] = [0, 16, 16, 20];

/** R24: 0 = einfache Aufgabe; oberstes Ticket mit Kindern 1; Kind min(3, Tiefe + 1). */
export function strokeCount(t: Pick<TicketKarte, "depth" | "subtaskTotal">): number {
  if (t.depth === 0) return t.subtaskTotal > 0 ? 1 : 0;
  return Math.min(MAX_STROKES, t.depth + 1);
}

export type DueMark = { text: string; art: "fehler" | "warnung" | null };

/** R15: „fällig Fr 30.10." — offen und überschritten Fehlerton, offen und heute Warnton, sonst neutral; erledigt ohne
 *  Ton. Unabhängig von der Wiedervorlage (E436). */
export function dueMark(dueDate: string | null, heute: string, erledigt: boolean): DueMark | null {
  if (dueDate === null) return null;
  const text = HIERARCHY_TEXT.due(tagBeschriftung(dueDate));
  if (erledigt) return { text, art: null };
  return { text, art: dueDate < heute ? "fehler" : dueDate === heute ? "warnung" : null };
}

/** R10: offen heißt „nicht in einer Spalte der Statusart DONE" (archiviert zählt als erledigt). */
export function isOpen(stateKind: string): boolean {
  return stateKind !== "DONE";
}
export function openChildRows(rows: readonly TicketSubtaskRow[]): TicketSubtaskRow[] {
  return rows.filter((r) => isOpen(r.stateKind));
}

/** R25: Kinder, die der Filter ausblendet — nicht archiviert und nicht auf dem Board. */
export function outsideFilter(card: TicketBoardCard, shown: ReadonlySet<string>): number {
  return card.subtasks.filter((z) => z.status !== "ARCHIVED" && !shown.has(z.id)).length;
}

type Benannt = Pick<TicketKarte, "displayNumber" | "title" | "parentTicketNumber" | "subtaskDone" | "subtaskTotal">;

/** R24: der zugängliche Name der Karte. */
export function cardName(t: Benannt): string {
  return [`${t.displayNumber} ${t.title}`,
          t.parentTicketNumber === null ? "" : HIERARCHY_TEXT.childOf(t.parentTicketNumber),
          t.subtaskTotal === 0 ? "" : HIERARCHY_TEXT.progressSpoken(t.subtaskDone, t.subtaskTotal)]
    .filter((x) => x !== "").join(", ");
}

/** R11: das Sofort-Popup an der Anzeige-Nummer — die echte Nummer, bei Kindern mit Position und Elternticket. */
export function numberPopup(t: Pick<TicketKarte, "ticketNumber" | "displayNumber" | "parentTicketNumber">): string {
  if (t.parentTicketNumber === null) return HIERARCHY_TEXT.realNumber(t.ticketNumber);
  const pos = Number(t.displayNumber.slice(t.displayNumber.lastIndexOf(".") + 1));
  return HIERARCHY_TEXT.realNumberUnder(t.ticketNumber, pos, t.parentTicketNumber);
}

/** Der Name des Eigentümers für Sofort-Popups; „“, wenn er fehlt (dann bleibt die Rolle ungenannt, nie „Eigentümer “). */
function ownerName(o: TicketKarte["owner"]): string {
  return o.kind === "USER" ? o.user?.name ?? "" : o.kind === "TEAM" ? o.team?.name ?? "" : HIERARCHY_TEXT.system;
}

/** R28, Festlegung 23: das Sofort-Popup der Zuständig-Zelle — die Person UND die Rolle des Eigentümers. In der Akte steht
 *  nur der Kreis, der Name sonst nirgends. */
export function assigneePopup(t: Pick<TicketKarte, "assignee" | "owner">): string {
  const a = t.assignee;
  const eigentuemer = ownerName(t.owner);
  return [a === null ? HIERARCHY_TEXT.unassigned : HIERARCHY_TEXT.assigneeOf(a.email ? `${a.name} (${a.email})` : a.name),
          eigentuemer === "" ? "" : HIERARCHY_TEXT.ownerOf(eigentuemer)].filter((x) => x !== "").join(" · ");
}

export type NotificationRole = TicketDetail["notifications"]["roles"][number];

/** R34: das Sofort-Popup am Knopf „Nicht mehr beobachten". */
export function rolesText(n: TicketDetail["notifications"]): string {
  if (n.muted) return HIERARCHY_TEXT.muted;
  if (n.roles.length === 0) return HIERARCHY_TEXT.noRoles;
  const namen = n.roles.map((r) => HIERARCHY_TEXT.roleNames[r]);
  const liste = namen.length === 1 ? namen[0]! : `${namen.slice(0, -1).join(", ")} und ${namen.at(-1)!}`;
  return `${HIERARCHY_TEXT.rolesIntro}${liste}`;
}

type Eigentuemer = TicketKarte["owner"];

/** Festlegung 9 des Plans: der Wert im `Auswahlmenue` — `user:<id>`, `team:<id>`, `""` = „System". */
export function ownerValue(o: Eigentuemer): string {
  if (o.kind === "USER" && o.user !== null) return `user:${o.user.id}`;
  if (o.kind === "TEAM" && o.team !== null) return `team:${o.team.id}`;
  return "";
}
export function ownerFromValue(v: string): { ownerUserId: string | null; ownerTeamId: string | null } {
  if (v.startsWith("user:")) return { ownerUserId: v.slice(5), ownerTeamId: null };
  if (v.startsWith("team:")) return { ownerUserId: null, ownerTeamId: v.slice(5) };
  return { ownerUserId: null, ownerTeamId: null };
}

/**
 * 213, F134, R7, R12: der Hinweis „Beim Speichern verlieren den Zugriff" beim Teamwechsel — wer aktiv ist (`active`,
 * 7-3: Gesperrte werden nicht genannt), aber nicht unter den Zuständigen des NEUEN Teams steht (`teamSees`). Beim
 * Zuständigen zählt die Wahl und die gespeicherte Person (fiel die Wahl nach 7-2 auf „Niemand“ zurück, verliert die
 * gespeicherte trotzdem den Zugriff); `people` nennt sie beim Namen. Eine Eigentümer-Person ersetzt der Server (R7);
 * die Personen des Teilbaums (R12) ohne die schon Genannten. `null`, wenn niemand.
 */
export function accessLossText(d: Pick<TicketDetail, "assignee" | "owner" | "watchers" | "descendantPeople">,
  assigneeChoice: string, people: readonly { id: string; name: string }[], teamSees: ReadonlySet<string>,
  active: ReadonlySet<string>): string | null {
  const verliert = (id: string) => id !== "" && active.has(id) && !teamSees.has(id);
  const zustaendig = [...new Set([assigneeChoice, d.assignee?.id ?? ""])].filter(verliert)
    .map((id) => people.find((u) => u.id === id)?.name ?? "").filter((n) => n !== "");
  const beobachter = d.watchers.filter((w) => verliert(w.id)).map((w) => w.name);
  const eigentuemer = d.owner.user !== null && verliert(d.owner.user.id) ? HIERARCHY_TEXT.ownerOf(d.owner.user.name) : "";
  const genannt = new Set([assigneeChoice, d.assignee?.id ?? "", d.owner.user?.id ?? "", ...d.watchers.map((w) => w.id)]);
  const teilbaum = d.descendantPeople.filter((u) => verliert(u.id) && !genannt.has(u.id)).map((u) => u.name);
  const teile = [zustaendig.length === 0 ? "" : `Zuständig ${zustaendig.join(", ")}`,
                 eigentuemer,
                 beobachter.length === 0 ? "" : `Beobachter ${beobachter.join(", ")}`,
                 teilbaum.length === 0 ? "" : `${HIERARCHY_TEXT.section} ${teilbaum.join(", ")}`].filter((t) => t !== "");
  return teile.length === 0 ? null : `Beim Speichern verlieren den Zugriff: ${teile.join("; ")}.`;
}

/** Die `data-*` einer Team-Option im `Auswahlmenue` (me-auswahlmenue: `data-kuerzel` statt Initialen, W2). */
export function ownerOptionData(team: { id: string; shortCode: string; name: string }): Record<string, string> {
  return { "data-name": team.name, "data-kuerzel": team.shortCode, "data-nutzer-id": `team:${team.id}` };
}
